/**
 * brief-helpers — pure helpers shared by the per-pillar brief composers
 * (`compliance-brief.ts`, `security-brief.ts`, `identity-brief.ts`).
 *
 * `cap` was duplicated byte-for-byte across the three composers; the
 * seeded-variant helpers (`localDayKey`, `hash`, `pickVariant`) were removed
 * in Copy Voice Phase 2 once every composer became deterministic. The
 * pillar-specific bits (verdict tables, imperative-verb defaults, number-word
 * arrays) intentionally stay co-located with each composer so the editorial
 * voice for one pillar can shift without pulling on the others. Only the
 * truly mechanical helpers live here.
 *
 * The one exception to "mechanical only" is `LEDE_FRAGMENTS` and its three
 * builders (blueprint §6.2): the MFA, failing-controls and drifted-resources
 * ledes are spoken by two composers each (the Command Center about a pillar,
 * the pillar page about itself), so they live here once and cannot drift
 * apart. Each composer exposes the table through its own `__TEST_ONLY__`, so
 * `brief-copy-lint.test.ts` sweeps it with that composer.
 */

import { formatAssessedClock } from '@/components/common/LastAssessed'

/** Capitalize the first character of a non-empty string. */
export function cap(s: string): string {
  if (!s) return s
  return s[0].toUpperCase() + s.slice(1)
}

/**
 * Buckets a 30-day score delta into a five-state direction so every brief slot
 * that reads motion (verdict, headline) routes off the same signal, instead
 * of each composer re-implementing the same gates.
 *
 *   `risingFast`  delta ≥ +3
 *   `rising`      0 < delta < +3
 *   `flat`        delta === 0
 *   `slipping`    -3 ≤ delta < 0
 *   `falling`     delta < -3
 *
 * Coherence rule: any slot that names motion (verdict "Score down 2 this
 * month.", headline "Keep the usual review cadence") must consult this
 * bucket — never just the score band — or two slots can disagree about which
 * way the score is moving.
 */
export type DeltaBucket = 'risingFast' | 'rising' | 'flat' | 'slipping' | 'falling'

export function bucketDelta(deltaValue: number): DeltaBucket {
  if (deltaValue >= 3) return 'risingFast'
  if (deltaValue > 0) return 'rising'
  if (deltaValue === 0) return 'flat'
  if (deltaValue >= -3) return 'slipping'
  return 'falling'
}

/* -------------------------------------------------------------------------- */
/* Shared lede fragments (blueprint §6.2, run 2a)                             */
/* -------------------------------------------------------------------------- */

/**
 * Lede fragments shared across composers. A lede is one sentence with exactly
 * one `**bold**` run naming the subject and its count (§4.7, lint L4): the
 * bold head comes from the first block, an optional unbolded clause from the
 * second, and the composer closes the sentence. Counts render as digits, as
 * the Carbon mockups do ("20 members", "14 controls").
 *
 * Wording follows the data, not the mockups: "Global admins", because
 * `noMfaGlobalAdmins` counts Global Administrators only (§7 C3); "resources",
 * because `activeDrifts` counts resources with drift (§7 C10); "your
 * baseline", because nothing records an approval (§7 B11, D33); and
 * "critical", never "SHALL" (D33).
 */
export const LEDE_FRAGMENTS = {
  MFA_ONE: '**1 member still signs in without MFA**',
  MFA_FEW: '**{n} members still sign in without MFA**',
  GA_SAME: ', and that member is a Global admin',
  GA_ONE: ', and 1 is a Global admin',
  GA_FEW: ', and {g} are Global admins',
  FAILING_NONE: '**No controls are failing**',
  FAILING_ONE: '**1 control is failing**',
  FAILING_FEW: '**{f} controls are failing**',
  CRITICAL_SAME: ', and it is critical',
  CRITICAL_ONE: ', and 1 of them is critical',
  CRITICAL_FEW: ', and {c} of them are critical',
  CRITICAL_OPEN_NONE: '**No critical controls are open**',
  CRITICAL_OPEN_ONE: '**1 critical control is still open**',
  CRITICAL_OPEN_FEW: '**{c} critical controls are still open**',
  DRIFTED_NONE: '**No resources have drifted from your baseline**',
  DRIFTED_ONE: '**1 resource has drifted from your baseline**',
  DRIFTED_FEW: '**{r} resources have drifted from your baseline**',
}

/** A finite, non-negative count, or `undefined`. An unknown count is never zero. */
export function knownCount(n: number | null | undefined): number | undefined {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : undefined
}

/**
 * "**20 members still sign in without MFA**, and 2 are Global admins" (no
 * closing period). `undefined` unless `members` is a known count of at least
 * 1. The Global admin clause appears only when 0 < admins <= members: a larger
 * admin count is a different population, so the clause would mislead.
 */
export function mfaLedeBody(members: number | undefined, globalAdmins?: number): string | undefined {
  const n = knownCount(members)
  if (n === undefined || n < 1) return undefined
  const head = n === 1 ? LEDE_FRAGMENTS.MFA_ONE : LEDE_FRAGMENTS.MFA_FEW.replace('{n}', String(n))
  const g = knownCount(globalAdmins)
  if (g === undefined || g < 1 || g > n) return head
  if (n === 1) return head + LEDE_FRAGMENTS.GA_SAME
  if (g === 1) return head + LEDE_FRAGMENTS.GA_ONE
  return head + LEDE_FRAGMENTS.GA_FEW.replace('{g}', String(g))
}

/**
 * "**14 controls are failing**, and 3 of them are critical" (no closing
 * period). The critical clause is dropped when the critical count is 0 or
 * unknown. When the critical count exceeds the failing count the two come
 * from different populations, so the failing total is dropped and the critical
 * count alone carries the lede, as it does with no failing count (or a zero
 * failing count beside open critical controls): "**3 critical controls are
 * still open**". That way a "Close the 9 critical controls first." headline
 * never sits over a smaller total. `undefined` when neither is known.
 */
export function controlsLedeBody(failing: number | undefined, critical?: number): string | undefined {
  const f = knownCount(failing)
  const c = knownCount(critical)
  if (f !== undefined && f >= 1 && !(c !== undefined && c > f)) {
    const head = f === 1 ? LEDE_FRAGMENTS.FAILING_ONE : LEDE_FRAGMENTS.FAILING_FEW.replace('{f}', String(f))
    if (c === undefined || c < 1) return head
    if (f === 1) return head + LEDE_FRAGMENTS.CRITICAL_SAME
    if (c === 1) return head + LEDE_FRAGMENTS.CRITICAL_ONE
    return head + LEDE_FRAGMENTS.CRITICAL_FEW.replace('{c}', String(c))
  }
  if (f === 0 && (c === undefined || c === 0)) return LEDE_FRAGMENTS.FAILING_NONE
  if (c === undefined) return undefined
  if (c === 0) return LEDE_FRAGMENTS.CRITICAL_OPEN_NONE
  if (c === 1) return LEDE_FRAGMENTS.CRITICAL_OPEN_ONE
  return LEDE_FRAGMENTS.CRITICAL_OPEN_FEW.replace('{c}', String(c))
}

/**
 * "**6 resources have drifted from your baseline**" (no closing period), or
 * the zero form. `undefined` when the count is unknown.
 */
export function driftedLedeBody(resources: number | undefined): string | undefined {
  const r = knownCount(resources)
  if (r === undefined) return undefined
  if (r === 0) return LEDE_FRAGMENTS.DRIFTED_NONE
  if (r === 1) return LEDE_FRAGMENTS.DRIFTED_ONE
  return LEDE_FRAGMENTS.DRIFTED_FEW.replace('{r}', String(r))
}

/**
 * The brief's provenance line (CLAUDE.md Product Voice rule 4, blueprint D36),
 * which composers emit in their `byline` field for PillarBrief to draw beside
 * the iuvo mark: "Assessed by Guardian at 06:12 today · 37 apps". It names
 * Guardian and the time, never the viewer (rule 5 bans bylines). The clock is
 * the Command Center's (`formatAssessedClock`), so every brief reads alike.
 *
 * `verb` follows the data's origin (terminology decision 5): "Assessed" when
 * Guardian evaluated it, "Synced" when it pulled Microsoft data as is (Secure
 * Score, the OAuth app inventory).
 *
 * `at`: an ISO time; `null` or "" when it never ran ("Awaiting first
 * assessment" / "Awaiting first sync"); `undefined` while unknown, which
 * returns "" so a loading page claims nothing and draws no line.
 */
export function briefProvenance(
  at: string | null | undefined,
  opts: { verb?: 'Assessed' | 'Synced'; scope?: string; now?: Date } = {},
): string {
  if (at === undefined) return ''
  const verb = opts.verb ?? 'Assessed'
  if (!at) return verb === 'Synced' ? 'Awaiting first sync' : 'Awaiting first assessment'
  const clock = formatAssessedClock(at, opts.now)
  const lead = clock ? `${verb} by Guardian ${clock}` : `${verb} by Guardian`
  return opts.scope ? `${lead} · ${opts.scope}` : lead
}

/* -------------------------------------------------------------------------- */
/* The first assessment (PR #152 finding 5, Adam 2026-10-06)                  */
/* -------------------------------------------------------------------------- */

/** A tenant's scan schedule: 'on' when enabled and not Manual, 'off' when not,
 *  'pending' while it is loading, failed or not asked (`useTenantScanSchedule`). */
export type ScanScheduleState = 'on' | 'off' | 'pending'

/**
 * The never-scanned brief's headline and button, spoken alike by the Command
 * Center and Baseline Compliance, so they live here once. Each composer
 * exposes the table through its own `__TEST_ONLY__` for the copy lint.
 */
export const FIRST_ASSESSMENT = {
  /** A Platform Admin, the one role that may run a scan (Ad-Hoc Scans). */
  RUN: 'Run the first assessment.',
  RUN_BUTTON: 'Open Ad-Hoc Scans',
  /** Schedule known off, and the viewer may edit it (Tenant Management). */
  TURN_ON: 'Turn on the scan schedule.',
  TURN_ON_BUTTON: 'Open tenant settings',
  /** Schedule known off, and the viewer may not edit it (or the role is unknown). */
  ASK: 'Ask your administrator to turn on scanning.',
  /** Schedule known on. */
  SCHEDULED: 'Check back after the first scheduled assessment.',
  /** Schedule pending: names the event, claims no schedule. */
  PENDING: 'Check back after the first assessment.',
}

/**
 * The never-scanned headline and its one button. `scanHref` (Ad-Hoc Scans)
 * comes only for a Platform Admin and wins; `settingsHref` (Tenant Management)
 * only for a viewer who may edit the scan schedule. The turn-on lines speak
 * only when the schedule is known off; a pending (or omitted) schedule gets
 * the neutral line, so no viewer is sent to switch on a schedule that may
 * already be on.
 */
export function firstAssessmentAction(opts: {
  scanHref?: string
  settingsHref?: string
  schedule?: ScanScheduleState
}): { headline: string; primaryAction?: { label: string; href: string } } {
  const scan = opts.scanHref?.trim()
  if (scan) return { headline: FIRST_ASSESSMENT.RUN, primaryAction: { label: FIRST_ASSESSMENT.RUN_BUTTON, href: scan } }
  if (opts.schedule === 'on') return { headline: FIRST_ASSESSMENT.SCHEDULED }
  if (opts.schedule !== 'off') return { headline: FIRST_ASSESSMENT.PENDING }
  const settings = opts.settingsHref?.trim()
  return settings
    ? { headline: FIRST_ASSESSMENT.TURN_ON, primaryAction: { label: FIRST_ASSESSMENT.TURN_ON_BUTTON, href: settings } }
    : { headline: FIRST_ASSESSMENT.ASK }
}

/** True for any non-negative direction (rising, risingFast, flat). */
export function isNonNegative(bucket: DeltaBucket): boolean {
  return bucket === 'rising' || bucket === 'risingFast' || bucket === 'flat'
}

/** True when posture is actually declining (slipping or falling). */
export function isNegative(bucket: DeltaBucket): boolean {
  return bucket === 'slipping' || bucket === 'falling'
}

/**
 * Format an ISO timestamp as a relative-time phrase for PillarBrief's
 * provenance row, e.g. "Updated 12 minutes ago" or "Updated just now".
 *
 * Tone: lead with the verb ("Updated"), not the system. Mirrors the existing
 * Command Center / Operations panel sync-age framing while staying inside the
 * advisor voice (CLAUDE.md Product Voice rule #4: byline carries provenance +
 * scope; no naked "Last sync: 2026-05-12T14:23:00Z" chips).
 *
 * Inputs and returns:
 *   - `null`, `undefined`, empty string, or unparseable string → `null`.
 *     Callers should render a "never synced" copy line of their own choosing
 *     (e.g. "Awaiting first sync.") so we don't bake the grace copy into a
 *     pure formatter.
 *   - Otherwise returns a string starting with "Updated".
 *
 * Pure: returns a fresh string each call so the formatter is safe for unit
 * tests. Callers that need "now" injected for testing can pass `asOf`.
 */
export function formatRelativeTime(
  timestamp: string | null | undefined,
  asOf: Date = new Date(),
): string | null {
  if (!timestamp) return null
  const t = new Date(timestamp).getTime()
  if (Number.isNaN(t)) return null

  const ageMs = Math.max(0, asOf.getTime() - t)
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day

  if (ageMs < minute) return 'Updated just now'

  if (ageMs < hour) {
    const m = Math.floor(ageMs / minute)
    return `Updated ${m} minute${m === 1 ? '' : 's'} ago`
  }

  if (ageMs < day) {
    const h = Math.floor(ageMs / hour)
    return `Updated ${h} hour${h === 1 ? '' : 's'} ago`
  }

  if (ageMs < week) {
    const d = Math.floor(ageMs / day)
    return `Updated ${d} day${d === 1 ? '' : 's'} ago`
  }

  // Beyond a week, fall back to a calendar phrasing so the editorial voice
  // doesn't strain into "Updated 47 days ago". Reads like a wire byline.
  const fmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' })
  return `Updated ${fmt.format(new Date(t))}`
}

/** Imperative verbs that open a control title ("Enforce conditional access"). */
const CONTROL_VERBS = new Set([
  'add', 'apply', 'audit', 'block', 'close', 'configure', 'create', 'deploy', 'disable', 'enable', 'enforce',
  'ensure', 'fix', 'limit', 'migrate', 'remove', 'require', 'restore', 'restrict', 'review', 'rotate', 'set',
  'turn', 'update', 'use',
])

/** The primary button copy for a control-led hero (Security Center, Best Practice). */
export interface ControlPrimaryCopy {
  FIX: string
  FAILING_ONE: string
  FAILING_FEW: string
  FAILING_ANY: string
}

/**
 * "Fix {title}" for a noun-led title, the title as is when it already starts
 * with a verb, else the failing-controls label (count only when known and 1+).
 * Titles come from the ranker, so trailing punctuation is trimmed.
 */
export function controlPrimaryLabel(
  copy: ControlPrimaryCopy,
  title: string | undefined,
  failing: number | undefined,
): string {
  const t = title?.trim().replace(/[.!?]+$/, '')
  if (t) {
    const first = t.split(/\s+/)[0].toLowerCase()
    return CONTROL_VERBS.has(first) ? cap(t) : copy.FIX.replace('{title}', t)
  }
  const n = knownCount(failing)
  if (n !== undefined && n >= 1) return n === 1 ? copy.FAILING_ONE : copy.FAILING_FEW.replace('{n}', String(n))
  return copy.FAILING_ANY
}
